import React, { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DayBadge, DAY, FaceBadge, FlagBadges, StatusBadge } from '@/components/staffAttendance/Badges';
import { CollarSelect, SectionSelect, WorkerTypeSelect } from '@/components/staffAttendance/SectionSelect';
import { CollarTiles } from '@/components/staffAttendance/CollarTiles';
import { SessionsButton } from '@/components/staffAttendance/SessionsDialog';
import { FoodAllowanceDialog } from '@/components/staffAttendance/FoodAllowanceDialog';
import { matchStaff, SearchNote } from '@/components/staffAttendance/StaffSearch';
import { staffAttApi, errMsg, fmtTime, todayIST } from '@/services/staffAttendanceApi';
import { downloadCsv } from '@/services/transportApi';

const EXTRA = { late: { label: 'Late', cls: 'bg-rose-100 text-rose-700' }, off_site: { label: 'Off-site', cls: 'bg-slate-800 text-white' } };
const SHIFTS = [['all', 'All shifts'], ['day', 'Day shift'], ['night', 'Night shift']];

const PunchCell = ({ p, testId }) => p ? (
  <div className="space-y-0.5" data-testid={testId}>
    <div className="font-mono">{fmtTime(p.time)}</div>
    <div className="flex flex-wrap gap-1"><StatusBadge status={p.status} />{p.source !== 'transport' && <FaceBadge result={p.face_result} />}<FlagBadges p={p} testId={testId} />
      {p.source === 'hr_correction' && <span className="rounded-full bg-indigo-100 text-indigo-700 px-2 py-0.5 text-xs font-semibold" data-testid={`${testId}-hr-fix`}>HR fix</span>}
      {p.source === 'transport' && <span className="rounded-full bg-sky-100 text-sky-800 px-2 py-0.5 text-xs font-semibold" data-testid={`${testId}-transport`}>Transport</span>}
      {p.auto_closed && <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${p.needs_hr ? 'bg-red-100 text-red-700' : 'bg-violet-100 text-violet-800'}`} data-testid={`${testId}-auto-closed`}>{p.needs_hr ? 'Contact HR' : 'Auto-closed 12 AM'}</span>}
      {p.after_midnight && <span className="rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-xs font-semibold" data-testid={`${testId}-ot`}>After midnight</span>}</div>
    <div className="text-[11px] text-gray-500" data-testid={`${testId}-where`}>{p.on_site === false && p.distance_m != null && p.nearest_location ? `${p.distance_m} m from ${p.nearest_location}` : (p.location_name || p.office || '')}{(p.office || p.network) ? ` · ${p.office || p.network}` : ''}</div>
    {p.decided_by_name && <div className="text-[11px] text-gray-500">{p.status === 'approved' ? 'Approved' : 'Rejected'} by {p.decided_by_name}{p.reason ? ` — ${p.reason}` : ''}</div>}
  </div>
) : <span className="text-gray-300">—</span>;

const Export = ({ date, section, workerType, collar }) => {
  const [from, setFrom] = useState(`${date.slice(0, 8)}01`);
  const [to, setTo] = useState(date);
  const [shift, setShift] = useState('all');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setFrom(`${date.slice(0, 8)}01`); setTo(date); }, [date]);
  const run = async () => {
    setBusy(true);
    const q = `date_from=${from}&date_to=${to}${section ? `&section=${encodeURIComponent(section)}` : ''}${workerType ? `&worker_type=${workerType}` : ''}${collar ? `&collar=${collar}` : ''}${shift !== 'all' ? `&shift=${shift}` : ''}`;
    try { await downloadCsv(`/staff-attendance/export?${q}`, `staff_attendance_${from}_to_${to}${shift !== 'all' ? `_${shift}_shift` : ''}.csv`); }
    catch { toast.error('Export failed — up to 92 days at a time'); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 ml-auto" data-testid="daily-export-bar">
      <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[150px] bg-white" data-testid="daily-export-from" />
      <span className="text-gray-400">→</span>
      <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[150px] bg-white" data-testid="daily-export-to" />
      <Select value={shift} onValueChange={setShift}>
        <SelectTrigger className="w-[150px] bg-white" data-testid="daily-export-shift"><SelectValue /></SelectTrigger>
        <SelectContent>{SHIFTS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`daily-export-shift-${v}`}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <Button onClick={run} disabled={busy || !from || !to || from > to} variant="outline" className="border-amber-400 text-amber-700" data-testid="daily-export-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Download CSV
      </Button>
    </div>
  );
};

const StaffCell = ({ r }) => (
  <TableCell>
    <div className="font-semibold">{r.staff_name}</div>
    <div className="text-xs text-gray-500">{r.staff_linked === false ? 'No Staff ID' : r.staff_id} · {r.section}</div>
    {r.from_section && <span className="inline-block mt-0.5 rounded-full bg-indigo-100 text-indigo-700 px-2 py-0.5 text-[11px] font-semibold" data-testid={`daily-from-${r.staff_id}`}>from {r.from_section}</span>}
    <div className="text-[11px] text-gray-400" data-testid={`daily-meta-${r.staff_id}`}>{r.worker_type ? `${r.worker_type[0].toUpperCase()}${r.worker_type.slice(1)} · ` : ''}{r.collar ? `${r.collar === 'white' ? 'White' : 'Blue'} collar · ` : ''}{r.shift?.startsWith('Night') ? r.shift : `Shift ${r.shift}`}{r.special_day && <span className="ml-1 rounded bg-amber-100 px-1 font-semibold text-amber-800" data-testid={`daily-special-${r.staff_id}`}>Special Day · {r.special_day}</span>}</div>
  </TableCell>
);

export const DailySection = ({ sections, search = '' }) => {
  const [date, setDate] = useState(todayIST());
  const [section, setSection] = useState('');
  const [wt, setWt] = useState('');
  const [collar, setCollar] = useState('');
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    staffAttApi.daily({ date, section: section || undefined, worker_type: wt || undefined, collar: collar || undefined })
      .then((d) => alive && setData(d)).catch((e) => alive && toast.error(errMsg(e, 'Failed to load daily attendance')));
    return () => { alive = false; };
  }, [date, section, wt, collar]);
  const chips = { ...DAY, ...EXTRA };
  const rows = (data?.rows || []).filter((r) => matchStaff(search, r.staff_name, r.staff_id));

  return (
    <div className="space-y-4" data-testid="daily-section">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="w-[160px] bg-white" data-testid="daily-date-input" />
        <SectionSelect sections={sections} value={section} onChange={setSection} testId="daily-section-select" />
        <WorkerTypeSelect value={wt} onChange={setWt} testId="daily-worker-select" />
        <CollarSelect value={collar} onChange={setCollar} testId="daily-collar-select" />
        <FoodAllowanceDialog date={date} section={section} />
        <Export date={date} section={section} workerType={wt} collar={collar} />
      </div>
      {data && <CollarTiles split={data.collar_split} value={collar} onPick={setCollar} metric="Present" sub={(s) => `of ${s.staff} staff`} testId="daily-collar-tiles" />}
      {data && (
        <div className="flex flex-wrap gap-2" data-testid="daily-counts">
          {Object.keys(chips).map((k) => <span key={k} className={`rounded-full px-3 py-1 text-sm font-semibold ${chips[k].cls}`} data-testid={`daily-count-${k}`}>{chips[k].label}: {data.counts[k] || 0}</span>)}
        </div>
      )}
      {data && <SearchNote q={search} shown={rows.length} total={data.rows.length} testId="daily-search-note" />}
      <Card className="border-0 shadow-md"><CardContent className="p-0 overflow-x-auto">
        <Table data-testid="daily-table">
          <TableHeader><TableRow>
            <TableHead>Staff</TableHead><TableHead>Status</TableHead><TableHead>First In</TableHead><TableHead>Last Out</TableHead><TableHead>Sessions</TableHead><TableHead className="text-right">Hours</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.staff_id} data-testid={`daily-row-${r.staff_id}`}>
                <StaffCell r={r} />
                <TableCell><DayBadge status={r.status} testId={`daily-status-${r.staff_id}`} /></TableCell>
                <TableCell><PunchCell p={r.check_in} testId={`daily-in-${r.staff_id}`} /></TableCell>
                <TableCell><PunchCell p={r.check_out} testId={`daily-out-${r.staff_id}`} /></TableCell>
                <TableCell><SessionsButton row={r} /></TableCell>
                <TableCell className="text-right font-mono" data-testid={`daily-hours-${r.staff_id}`}>{r.hours ?? '—'}</TableCell>
              </TableRow>
            ))}
            {!data && <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline text-amber-600" /></TableCell></TableRow>}
            {data && data.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8" data-testid="daily-empty">No staff for this filter.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
};

export default DailySection;
